import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

export type QuoteStatus =
  | 'pending'
  | 'quoted'
  | 'awaiting_contact'
  | 'contacted'
  | 'rejected'
  | 'canceled';

interface StatusBadgeProps {
  status: QuoteStatus;
  /** Optional timestamp; relative time (e.g. "2 days ago") is shown only when valid. */
  timestamp?: string | null;
  /** Translation namespace prefix (e.g. 'quote.status' or 'admin.quotes.status'). */
  tPrefix?: string;
  /** Additional className. */
  className?: string;
}

/**
 * Shared status badge used by both the client dashboard and admin quote lists.
 * Centralizes status styling, translation, and safe relative-date rendering.
 *
 * Kept under `shared/` because it is imported by both `components/user` and
 * `components/admin` surfaces.
 */
const STATUS_MODIFIER_CLASSES: Record<QuoteStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  quoted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  awaiting_contact: 'bg-sky-50 text-sky-700 border-sky-200',
  contacted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  canceled: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  timestamp,
  tPrefix = 'quote.status',
  className = '',
}) => {
  const { t } = useTranslation();

  // Only render the relative-time suffix when the timestamp parses to a real
  // date. This prevents "Invalid Date", "undefined ago", or "No days ago" from
  // ever appearing in the UI.
  const isValidTimestamp = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0 && !Number.isNaN(Date.parse(value));

  const showTimestamp = isValidTimestamp(timestamp);

  return (
    <Badge
      variant="outline"
      className={`text-xs font-medium whitespace-nowrap ${STATUS_MODIFIER_CLASSES[status] ?? ''} ${className}`}
    >
      {t(`${tPrefix}.${status}`, { defaultValue: status })}
      {showTimestamp && (
        <span className="ml-1.5 opacity-80">
          {' · '}
          {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
        </span>
      )}
    </Badge>
  );
};
